import React, { useState, useRef, useEffect } from 'react';
import { parseUserMessage } from '../utils/messageParser';
import { 
  searchByTitle, 
  searchByActor, 
  searchByDirector, 
  searchByTimeframe, 
  searchByKeywords,
  addDirectorInfo
} from '../utils/movieApi';
import Logger from '../utils/logger';

function ChatContainer({ showMovies, setProcessing }) {
  const [messages, setMessages] = useState([
    { sender: 'ai', text: "Hey there! I'm here to help you find the perfect movie. Just tell me what you're in the mood for." },
    { sender: 'ai', text: "I'll recommend something great!" }
  ]);
  const [inputValue, setInputValue] = useState('');
  const chatBoxRef = useRef(null);

  useEffect(() => {
    // Scroll to bottom whenever messages change
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const handleSendMessage = async () => {
    const message = inputValue.trim();
    if (message === '') return;

    // Check if message is too short (only one word)
    const wordCount = message.split(/\s+/).length;
    if (wordCount === 1) {
      const aiResponse = 'Could you please provide more details? For example, tell me about genres, themes, or the type of movie you\'re looking for. This will help me find better recommendations!';
      
      setMessages([
        ...messages,
        { sender: 'user', text: message },
        { sender: 'ai', text: aiResponse }
      ]);
      
      setInputValue('');
      await Logger.logInteraction(message, aiResponse, null);
      return;
    }

    // Show loading state
    setProcessing(true);

    // Add user message to chat
    setMessages(prev => [...prev, { sender: 'user', text: message }]);
    setInputValue('');

    try {
      // Parse message to extract search criteria
      const searchCriteria = await parseUserMessage(message);
      
      // Search for movie recommendations based on search criteria
      let searchResults = [];
      let aiResponse = '';
      let appliedFilters = [];

      // If searching for a specific title, prioritize that
      if (searchCriteria.title) {
        const titleSearchResult = await searchByTitle(searchCriteria.title);
        if (titleSearchResult.results.length > 0) {
          searchResults = titleSearchResult.results;
          aiResponse = titleSearchResult.message || `Found ${searchResults.length} movies matching "${searchCriteria.title}"`;
          
          // Update messages with AI response
          setMessages(prev => [...prev, { sender: 'ai', text: aiResponse }]);
          
          // Show movies
          showMovies(searchResults.slice(0, 8), searchResults);
          await Logger.logInteraction(message, aiResponse, searchResults);
          setProcessing(false);
          return;
        } else {
          aiResponse = titleSearchResult.message || `No movies found with title "${searchCriteria.title}"`;
          setMessages(prev => [...prev, { sender: 'ai', text: aiResponse }]);
        }
      }

      // Gather results from all criteria
      let yearResults = [];
      let genreResults = [];
      let actorResults = [];
      let directorResults = [];

      // Year search
      if (searchCriteria.year) {
        const yearSearchResult = await searchByTimeframe(searchCriteria.year);
        yearResults = yearSearchResult.results;
        if (yearResults.length > 0) {
          appliedFilters.push(searchCriteria.year.type === 'year' 
            ? `year: ${searchCriteria.year.value}`
            : `period: ${searchCriteria.year.start}-${searchCriteria.year.end}`);
        }
      }
      
      // Genre search
      if (searchCriteria.genre) {
        const genreSearchResult = await searchByKeywords(searchCriteria.genre);
        genreResults = genreSearchResult.results;
        if (genreResults.length > 0) {
          appliedFilters.push(`genre: ${searchCriteria.genre}`);
        }
      }
      
      // Actor search
      if (searchCriteria.actor) {
        const actorSearchResult = await searchByActor(searchCriteria.actor);
        actorResults = actorSearchResult.results;
        if (actorResults.length > 0) {
          appliedFilters.push(`actor: ${searchCriteria.actor}`);
        }
      }
      
      // Director search
      if (searchCriteria.director) {
        const directorSearchResult = await searchByDirector(searchCriteria.director);
        directorResults = directorSearchResult.results;
        if (directorResults.length > 0) {
          appliedFilters.push(`director: ${searchCriteria.director}`);
        }
      }

      // Apply multiple criteria by finding the intersection of results
      let combinedResults = [];
      
      // Determine which criteria have results
      const hasYearResults = yearResults.length > 0;
      const hasGenreResults = genreResults.length > 0;
      const hasActorResults = actorResults.length > 0;
      const hasDirectorResults = directorResults.length > 0;
      
      // If we have multiple criteria with results, find the intersection
      if ([hasYearResults, hasGenreResults, hasActorResults, hasDirectorResults].filter(Boolean).length > 1) {
        // Start with the first available results set
        if (hasYearResults) {
          combinedResults = [...yearResults];
        } else if (hasGenreResults) {
          combinedResults = [...genreResults];
        } else if (hasActorResults) {
          combinedResults = [...actorResults];
        } else if (hasDirectorResults) {
          combinedResults = [...directorResults];
        }
        
        // Filter by each criteria in turn
        if (hasYearResults && combinedResults !== yearResults) {
          combinedResults = combinedResults.filter(movie => 
            yearResults.some(m => m.id === movie.id)
          );
        }
        
        if (hasGenreResults && combinedResults !== genreResults) {
          combinedResults = combinedResults.filter(movie => 
            genreResults.some(m => m.id === movie.id)
          );
        }
        
        if (hasActorResults && combinedResults !== actorResults) {
          combinedResults = combinedResults.filter(movie => 
            actorResults.some(m => m.id === movie.id)
          );
        }
        
        if (hasDirectorResults && combinedResults !== directorResults) {
          combinedResults = combinedResults.filter(movie => 
            directorResults.some(m => m.id === movie.id)
          );
        }
        
        searchResults = combinedResults;
        
        // If we have multiple filters but no results, provide feedback
        if (searchResults.length === 0 && appliedFilters.length > 1) {
          aiResponse = `No movies found matching all criteria: ${appliedFilters.join(', ')}. Try broadening your search.`;
          setMessages(prev => [...prev, { sender: 'ai', text: aiResponse }]);
          
          // Offer some results from one of the criteria as fallback
          if (hasGenreResults) {
            searchResults = genreResults;
            aiResponse += ` Showing results matching only: genre: ${searchCriteria.genre}`;
            setMessages(prev => [...prev, { sender: 'ai', text: `Showing results matching only: genre: ${searchCriteria.genre}` }]);
          } 
          else if (hasYearResults) {
            searchResults = yearResults;
            aiResponse += ` Showing results matching only: ${appliedFilters[0]}`;
            setMessages(prev => [...prev, { sender: 'ai', text: `Showing results matching only: ${appliedFilters[0]}` }]);
          } 
          else if (hasActorResults) {
            searchResults = actorResults;
            aiResponse += ` Showing results matching only: actor: ${searchCriteria.actor}`;
            setMessages(prev => [...prev, { sender: 'ai', text: `Showing results matching only: actor: ${searchCriteria.actor}` }]);
          } 
          else if (hasDirectorResults) {
            searchResults = directorResults;
            aiResponse += ` Showing results matching only: director: ${searchCriteria.director}`;
            setMessages(prev => [...prev, { sender: 'ai', text: `Showing results matching only: director: ${searchCriteria.director}` }]);
          }
        } else if (searchResults.length > 0) {
          // Provide feedback about the multiple criteria
          aiResponse = `Found ${searchResults.length} movies matching all criteria: ${appliedFilters.join(', ')}`;
          setMessages(prev => [...prev, { sender: 'ai', text: aiResponse }]);
        }
      } else {
        // Only one criteria has results, use those
        if (hasYearResults) {
          searchResults = yearResults;
          aiResponse = `Found ${searchResults.length} movies from ${searchCriteria.year.type === 'year' ? searchCriteria.year.value : `${searchCriteria.year.start}-${searchCriteria.year.end}`}`;
        } else if (hasGenreResults) {
          searchResults = genreResults;
          aiResponse = `Found ${searchResults.length} movies matching genre: ${searchCriteria.genre}`;
        } else if (hasActorResults) {
          searchResults = actorResults;
          aiResponse = `Found ${searchResults.length} movies starring ${searchCriteria.actor}`;
        } else if (hasDirectorResults) {
          searchResults = directorResults;
          aiResponse = `Found ${searchResults.length} movies directed by ${searchCriteria.director}`;
        } else {
          aiResponse = 'No movies found matching your criteria. Please try a different search.';
        }

        if (searchResults.length > 0) {
          setMessages(prev => [...prev, { sender: 'ai', text: aiResponse }]);
        } else {
          setMessages(prev => [...prev, { sender: 'ai', text: 'No movies found matching your criteria. Please try a different search.' }]);
        }
      }

      if (searchResults.length > 0) {
        // Add director info if needed
        const moviesToDisplay = searchResults.slice(0, 8);
        const finalResults = await addDirectorInfo(moviesToDisplay);
        
        // Show movies
        showMovies(finalResults, searchResults);
        await Logger.logInteraction(message, aiResponse, searchResults);
      } else {
        await Logger.logInteraction(message, aiResponse, []);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorResponse = 'Sorry, I encountered an error. Please try again.';
      setMessages(prev => [...prev, { sender: 'ai', text: errorResponse }]);
      await Logger.logInteraction(message, errorResponse, null);
    } finally {
      // Hide loading state
      setProcessing(false);
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-box" id="chatBox" ref={chatBoxRef}>
        {messages.map((msg, index) => (
          <div key={index} className={`message ${msg.sender}`}>
            {msg.text}
          </div>
        ))}
      </div>
      <div className="input-area">
        <input
          type="text"
          id="userInput"
          placeholder="Type your message..."
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
        />
        <button onClick={handleSendMessage} id="send-btn">Find Movies</button>
      </div>
    </div>
  );
}

export default ChatContainer;
